import { projectFieldsToHighlight } from '../../components/project/project.type';

export const removeSpecialChars = (term: string) => term.replace(/[.+\-&|!(){}[\]^"~*?:\\/]/g, '');
export const addWildCard = (term: string) => `${term}*`;

export const PROJECT_NUMBER_PREFIXES = ['C', 'R', 'N'];

/**
 * Expands bare numeric search terms to also match prefixed project numbers.
 * e.g. "39102*" → ["39102*", "C39102*", "R39102*", "N39102*"]
 *
 * @param processedTerms - Terms that have already been through removeSpecialChars + addWildCard
 *                         (i.e. each term ends with "*"). Call order matters: this function
 *                         expects the wildcard to already be present.
 *
 * Terms with fewer than 3 digits are intentionally skipped to avoid over-broad expansions
 * for short/ambiguous inputs.
 *
 * Note: with searchMode "any", multi-word queries (e.g. "39102 Oakland") will match documents
 * containing *any* of the expanded tokens. This is the intended broad-match behaviour.
 */
export const expandProjectNumberTerms = (processedTerms: string[]): string[] => {
  // Requires 3+ digits so short inputs like "39*" don't get prefix-expanded
  const numericWithWildcard = /^\d{3,}\*$/;
  const result: string[] = [];
  for (const term of processedTerms) {
    result.push(term);
    if (numericWithWildcard.test(term)) {
      for (const prefix of PROJECT_NUMBER_PREFIXES) {
        result.push(`${prefix}${term}`); // e.g. prefix "C" + "39102*" → "C39102*"
      }
    }
  }
  return result;
};

export const preProcessSearchText = (searchText: string) => {
  const EMPTY_STRING = '';
  const SINGLE_SPACE = ' ';
  const multipleSpacesRegex = /\s+/g;
  const onlySpecialCharsRegex = /^[^a-zA-Z0-9]+$/;

  let result = searchText.trim();
  if (result === EMPTY_STRING) return EMPTY_STRING;

  result = result.replace(multipleSpacesRegex, SINGLE_SPACE);

  const resultIsOnlySpecialChars = onlySpecialCharsRegex.test(result);
  if (resultIsOnlySpecialChars) return EMPTY_STRING;

  const terms = result.split(SINGLE_SPACE);
  const processedTerms = terms.map(removeSpecialChars).map(addWildCard);
  const expandedTerms = expandProjectNumberTerms(processedTerms);
  result = expandedTerms.join(SINGLE_SPACE);

  return result;
};

export const buildSearchRequestBody = (
  searchQuery: string,
  filterArray: any,
  paginationModel: any,
  sortingModel: any,
  ignorePagination = false,
  visibleColumns?: string[],
) => {
  const filterString = filterArray && filterArray.length > 0 ? filterArray.join(' and ') : '';
  const skipValue = paginationModel.page * paginationModel.pageSize;
  const topValue = paginationModel.pageSize;
  const orderby =
    searchQuery != ''
      ? 'search.score() desc'
      : Array.isArray(sortingModel) && sortingModel.length > 0 && sortingModel[0].id
        ? `${sortingModel[0].id} ${sortingModel[0].desc ? 'desc' : 'asc'}`
        : 'CreatedOn desc';

  // For search fields, we want to include all searchable fields, not just visible columns
  // This ensures that search works across all relevant fields and highlighting works properly
  const searchableFields = [
    'ProjectTitle',
    'ProjectNumber',
    'CreatedBy',
    'ProjectType',
    'City',
    'Contractor',
    'County',
    'CustomArea',
    'PostalCode',
    'ConstructionType',
    'Electrical',
    'ExteriorWalls',
    'Foundation',
    'HVACScope',
    'HVACType',
    'Roof',
    'Superstructure',
    'UniqueCostFactors1',
    'UniqueCostFactors2',
    'UniqueCostFactors3',
    'OtherCostFactor',
    'Windows',
    'Borrower',
    'Client',
    'Observer',
    'Status',
    'ProjectTags',
  ];

  const requestBody = {
    count: true,
    ...(ignorePagination
      ? { skip: 0, top: 1000000 } // fetch all projects
      : { skip: skipValue, top: topValue }),
    searchMode: 'any',
    queryType: 'full',
    search: searchQuery,
    filter: filterString,
    select: '*',
    orderby: orderby,
    scoringProfile: 'search-visible-columns',
    highlight: projectFieldsToHighlight,
    // Add fields to highlight
    highlightPreTag: '<em>', // Tag to start highlighting
    highlightPostTag: '</em>', // Tag to end highlighting
    searchFields: searchableFields.join(','),
  };

  return requestBody;
};
